import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeleteExercisesBulk,
  useDeleteExercise,
  useGetAllExercises,
  useGetAllPlans,
  useUpdateExercise,
  useUploadImage,
} from "../../service/api";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { ExerciseRow } from "../../types";

const DIALOG_PAPER_SX = { backgroundColor: "#fff", color: "#000" };

export const ExercisesTable = () => {
  const { t } = useTranslation();
  const { data: exercises = [] } = useGetAllExercises();
  const { data: plans = [] } = useGetAllPlans();
  const { mutate: deleteExercise, isPending: deletingExercise } = useDeleteExercise();
  const { mutate: deleteExercisesBulk, isPending: deletingExercisesBulk } = useDeleteExercisesBulk();
  const { mutate: updateExercise, isPending: updatingExercise } = useUpdateExercise();
  const { mutate: uploadImage, isPending: uploadingImage } = useUploadImage();

  const [exerciseToDelete, setExerciseToDelete] = useState<ExerciseRow | null>(null);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState<string[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const editImageInputRef = useRef<HTMLInputElement>(null);
  const [editingExercise, setEditingExercise] = useState<ExerciseRow | null>(null);
  const [editExerciseData, setEditExerciseData] = useState<ExerciseRow | null>(null);
  const [editPendingImageFile, setEditPendingImageFile] = useState<File | null>(null);
  const [editImagePreviewUrl, setEditImagePreviewUrl] = useState<string | null>(null);

  const plansContainingExercise = exerciseToDelete
    ? plans.filter((p) => p.exercisesIds?.includes(exerciseToDelete.id))
    : [];

  const confirmDeleteExercise = () => {
    if (!exerciseToDelete) return;
    deleteExercise(exerciseToDelete.id, {
      onSuccess: () => setExerciseToDelete(null),
    });
  };

  const openEditExercise = (ex: ExerciseRow) => {
    setEditingExercise(ex);
    setEditExerciseData({ ...ex });
    setEditPendingImageFile(null);
    setEditImagePreviewUrl(ex.imageUrl || null);
  };

  const handleEditExerciseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditExerciseData((prev) => (prev ? { ...prev, [name]: name === "durationInSeconds" ? Number(value) : value } : prev));
  };

  const handleSaveExercise = () => {
    if (!editExerciseData) return;
    const doUpdate = (imageUrl: string) => {
      updateExercise({ id: editExerciseData.id, data: { ...editExerciseData, imageUrl } }, {
        onSuccess: () => { setEditingExercise(null); setEditExerciseData(null); setEditPendingImageFile(null); setEditImagePreviewUrl(null); },
      });
    };
    if (editPendingImageFile) {
      uploadImage(editPendingImageFile, { onSuccess: (url) => doUpdate(url) });
    } else {
      doUpdate(editExerciseData.imageUrl);
    }
  };

  const visibleExercises = exercises.filter((ex: ExerciseRow) => !ex.isDeleted);
  const visibleExerciseIds = visibleExercises.map((ex) => ex.id);
  const selectedVisibleCount = selectedExerciseIds.filter((id) => visibleExerciseIds.includes(id)).length;
  const allVisibleSelected = visibleExerciseIds.length > 0 && selectedVisibleCount === visibleExerciseIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const plansContainingSelectedExercises = plans.filter((plan) =>
    plan.exercisesIds?.some((exerciseId) => selectedExerciseIds.includes(exerciseId))
  );

  const toggleSelectAllExercises = (checked: boolean) => {
    setSelectedExerciseIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...visibleExerciseIds]));
      }
      return prev.filter((id) => !visibleExerciseIds.includes(id));
    });
  };

  const toggleSelectExercise = (id: string, checked: boolean) => {
    setSelectedExerciseIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((item) => item !== id)));
  };

  const confirmBulkDeleteExercises = () => {
    if (selectedExerciseIds.length === 0) return;
    deleteExercisesBulk(selectedExerciseIds, {
      onSuccess: () => {
        setBulkDeleteOpen(false);
        setSelectedExerciseIds([]);
      },
    });
  };

  return (
    <>
      <Dialog open={!!exerciseToDelete} onClose={() => setExerciseToDelete(null)} slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
        <DialogTitle sx={{ color: "#000" }}>{t("videoLibrary.exercisesTable.deleteTitle")}</DialogTitle>
        <DialogContent>
          {plansContainingExercise.length > 0 ? (
            <DialogContentText sx={{ color: "#333" }}>
              <strong>"{exerciseToDelete?.title}"</strong> {t("videoLibrary.exercisesTable.usedInPlansIntro", { count: plansContainingExercise.length })}{" "}
              <strong>{plansContainingExercise.map((p) => p.name).join(", ")}</strong>.
              <br /><br />
              {t("videoLibrary.exercisesTable.usedInPlansWarning")}
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ color: "#333" }}>
              {t("videoLibrary.exercisesTable.deleteConfirmPrefix")} <strong>"{exerciseToDelete?.title}"</strong>?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExerciseToDelete(null)}>{t("videoLibrary.common.cancel")}</Button>
          <Button onClick={confirmDeleteExercise} color="error" variant="contained" disabled={deletingExercise}>
            {deletingExercise ? t("videoLibrary.common.deleting") : t("videoLibrary.common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)} slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
        <DialogTitle sx={{ color: "#000" }}>{t("videoLibrary.exercisesTable.bulkDeleteTitle")}</DialogTitle>
        <DialogContent>
          {plansContainingSelectedExercises.length > 0 ? (
            <DialogContentText sx={{ color: "#333" }}>
              {t("videoLibrary.exercisesTable.bulkDeleteUsedInPlans", {
                count: selectedExerciseIds.length,
                plans: plansContainingSelectedExercises.map((plan) => plan.name).join(", "),
              })}
            </DialogContentText>
          ) : (
            <DialogContentText sx={{ color: "#333" }}>
              {t("videoLibrary.exercisesTable.bulkDeleteConfirm", { count: selectedExerciseIds.length })}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)}>{t("videoLibrary.common.cancel")}</Button>
          <Button onClick={confirmBulkDeleteExercises} color="error" variant="contained" disabled={deletingExercisesBulk}>
            {deletingExercisesBulk ? t("videoLibrary.common.deleting") : t("videoLibrary.common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editingExercise} onClose={() => setEditingExercise(null)} fullWidth maxWidth="sm" slotProps={{ paper: { sx: DIALOG_PAPER_SX } }}>
        <DialogTitle sx={{ color: "#000" }}>{t("videoLibrary.exercisesTable.editTitle")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField name="title" label={t("videoLibrary.common.titleField")} value={editExerciseData?.title ?? ""} onChange={handleEditExerciseChange} required size="small" sx={{ flex: 2 }} />
            <TextField name="durationInSeconds" label={t("videoLibrary.common.durationSecondsField")} type="number" value={editExerciseData?.durationInSeconds ?? 0} onChange={handleEditExerciseChange} onFocus={(e) => e.target.select()} required size="small" sx={{ flex: 1 }} />
          </Box>
          <TextField name="instruction" label={t("videoLibrary.common.instructionField")} value={editExerciseData?.instruction ?? ""} onChange={handleEditExerciseChange} required size="small" />
          <TextField name="videoUrl" label={t("videoLibrary.common.videoUrlField")} value={editExerciseData?.videoUrl ?? ""} onChange={handleEditExerciseChange} required size="small" />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <input
              ref={editImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setEditPendingImageFile(file);
                setEditImagePreviewUrl(URL.createObjectURL(file));
                e.target.value = "";
              }}
            />
            <Button variant="contained" size="small" onClick={() => editImageInputRef.current?.click()} sx={{ whiteSpace: "nowrap", flexShrink: 0, color: "#fff" }}>
              {editPendingImageFile ? t("videoLibrary.common.changeImage") : t("videoLibrary.common.uploadImage")}
            </Button>
            {editImagePreviewUrl ? (
              <Tooltip title={editPendingImageFile?.name ?? t("videoLibrary.common.currentImage")} placement="top">
                <Box component="img" src={editImagePreviewUrl} alt="preview" sx={{ height: 36, width: 36, objectFit: "cover", borderRadius: 1, border: "1px solid #ddd", flexShrink: 0 }} />
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.secondary">{t("videoLibrary.common.noImage")}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingExercise(null)}>{t("videoLibrary.common.cancel")}</Button>
          <Button
            onClick={handleSaveExercise}
            variant="contained"
            disabled={updatingExercise || uploadingImage || !editExerciseData?.title.trim() || !editExerciseData?.instruction.trim() || !editExerciseData?.videoUrl.trim() || (editExerciseData?.durationInSeconds ?? 0) <= 0}
            sx={{ color: "#fff" }}
          >
            {uploadingImage ? t("videoLibrary.common.uploading") : updatingExercise ? t("videoLibrary.common.saving") : t("videoLibrary.common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={2} sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", backgroundColor: "#fff", color: "#000" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexShrink: 0, mb: 1 }}>
          <Typography variant="h6" fontWeight="bold">{t("videoLibrary.exercisesTable.heading")}</Typography>
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setBulkDeleteOpen(true)}
            disabled={selectedExerciseIds.length === 0}
          >
            {t("videoLibrary.common.deleteSelected", { count: selectedExerciseIds.length })}
          </Button>
        </Box>
        <Divider sx={{ mb: 1, flexShrink: 0 }} />
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={(e) => toggleSelectAllExercises(e.target.checked)}
                    inputProps={{ "aria-label": t("videoLibrary.common.selectAll") }}
                  />
                </TableCell>
                <TableCell>{t("videoLibrary.exercisesTable.colTitle")}</TableCell>
                <TableCell>{t("videoLibrary.exercisesTable.colDuration")}</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleExercises.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{t("videoLibrary.exercisesTable.empty")}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleExercises.map((ex: ExerciseRow) => (
                  <TableRow key={ex.id} hover selected={selectedExerciseIds.includes(ex.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedExerciseIds.includes(ex.id)}
                        onChange={(e) => toggleSelectExercise(ex.id, e.target.checked)}
                        inputProps={{ "aria-label": t("videoLibrary.common.selectRow") }}
                      />
                    </TableCell>
                    <TableCell>{ex.title}</TableCell>
                    <TableCell>{ex.durationInSeconds}</TableCell>
                    <TableCell padding="checkbox">
                      <Box sx={{ display: "flex" }}>
                        <Tooltip title={t("videoLibrary.exercisesTable.editTooltip")}>
                          <IconButton size="small" onClick={() => openEditExercise(ex)} sx={{ color: "#1976d2" }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("videoLibrary.exercisesTable.deleteTooltip")}>
                          <IconButton size="small" onClick={() => setExerciseToDelete(ex)} sx={{ color: "#d32f2f" }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};
